import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Building2, Eye, Settings, X, Save, LayoutGrid, List, FileText, SlidersHorizontal, ChevronDown, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../hooks/useAuth';
import { useBusiness } from '../contexts/BusinessContext';
import { canViewFullCustomerPhone } from '../utils/roleUtils';
import { displayCustomerPhone } from '../utils/phoneFormatUtils';
import { apiService } from '../services/api';
import { QuoteRow } from '../types';
import { formatQuoteStatusLabel, getQuoteDisplayStatus, QUOTE_APPOINTMENT_CANCELED_FILTER, QUOTE_APPOINTMENT_CREATED_FILTER, QUOTE_OFFER_EXPIRED_FILTER, QUOTE_PENDING_DEPOSIT_FILTER } from '../utils/quoteDisplayStatus';
import LoadingSpinner from './LoadingSpinner';

const formatRequestNumber = (quoteId: string): string => {
  const first8 = (quoteId || '').slice(0, 8);
  return first8 ? `Q-${first8}` : '—';
};

const formatPreferredSchedule = (preferredSchedule: string | null | undefined): string => {
  if (!preferredSchedule) return '—';
  const d = new Date(preferredSchedule);
  if (Number.isNaN(d.getTime())) return preferredSchedule;
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

const formatSubmittedDate = (createdAt: string): string => {
  if (!createdAt) return '—';
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return '—';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
};

const formatSubmittedTime = (createdAt: string): string => {
  if (!createdAt) return '—';
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
};

const getQuoteTypeNames = (quote: QuoteRow): string[] => {
  if (!quote?.services?.length) return [];
  const names = quote.services
    .map((s) => (s.name || '').trim())
    .filter(Boolean);
  return names.filter((n, i) => names.indexOf(n) === i);
};

const formatStatusLabel = formatQuoteStatusLabel;

const formatMoney = (n: number | null | undefined): string =>
  n != null && Number.isFinite(n)
    ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n)
    : '—';

const getCustomerDisplayName = (quote: QuoteRow): string => {
  const firstName = (quote.customer_first_name || '').trim();
  const lastName = (quote.customer_last_name || '').trim();
  if (firstName && lastName) return `${firstName} ${lastName.charAt(0)}.`;
  return lastName || firstName || '—';
};

const getCustomerDisplayLine = (quote: QuoteRow, canViewFullPhone: boolean): string => {
  const name = getCustomerDisplayName(quote);
  const phone = displayCustomerPhone(quote.customer_phone, canViewFullPhone);
  if (phone && name !== '—') return `${name} - ${phone}`;
  if (phone) return phone;
  return name;
};

type SortOption = 'newest' | 'oldest';
type QuoteViewMode = 'grid' | 'table';

interface QuoteRequestsSectionProps {
  offerSettingsModalOpen?: boolean;
  onOfferSettingsModalOpenChange?: (open: boolean) => void;
}

const QuoteRequestsSection: React.FC<QuoteRequestsSectionProps> = ({
  offerSettingsModalOpen: offerSettingsModalOpenProp,
  onOfferSettingsModalOpenChange,
}) => {
  const { currentBusiness } = useBusiness();
  const { user } = useAuth();
  const canViewFullPhone = canViewFullCustomerPhone(user, currentBusiness?.id);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState<QuoteRow[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sortOrder, setSortOrder] = useState<SortOption>('newest');
  const [isFilterSectionOpen, setIsFilterSectionOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [quoteView, setQuoteView] = useState<QuoteViewMode>(() => {
    try {
      if (typeof window !== 'undefined' && window.innerWidth < 640) return 'grid';
      const saved = localStorage.getItem('quoteRequestsView');
      return saved === 'grid' ? 'grid' : 'table';
    } catch {
      return 'table';
    }
  });
  const [limit] = useState(25);
  const [offset, setOffset] = useState(0);
  const [paginationTotal, setPaginationTotal] = useState(0);
  const [isOfferSettingsModalOpenInternal, setIsOfferSettingsModalOpenInternal] = useState(false);
  const isOfferSettingsModalControlled = onOfferSettingsModalOpenChange !== undefined;
  const isOfferSettingsModalOpen = isOfferSettingsModalControlled
    ? (offerSettingsModalOpenProp ?? false)
    : isOfferSettingsModalOpenInternal;
  const setIsOfferSettingsModalOpen = isOfferSettingsModalControlled
    ? onOfferSettingsModalOpenChange!
    : setIsOfferSettingsModalOpenInternal;
  const [offerSettingsLoading, setOfferSettingsLoading] = useState(false);
  const [offerSettingsSaving, setOfferSettingsSaving] = useState(false);
  const [offerSettingsDraft, setOfferSettingsDraft] = useState<{
    requireDepositByDefault: boolean;
    defaultDepositAmount: string;
    notifyOwnerOnQuoteChatMessage: boolean;
  }>({ requireDepositByDefault: true, defaultDepositAmount: '50', notifyOwnerOnQuoteChatMessage: true });

  const loadOfferSettings = useCallback(async () => {
    if (!currentBusiness?.id) return;
    try {
      setOfferSettingsLoading(true);
      const data = await apiService.getOfferSettings(currentBusiness.id);
      setOfferSettingsDraft({
        requireDepositByDefault: data.requireDepositByDefault,
        defaultDepositAmount: String(data.defaultDepositAmount ?? 50),
        notifyOwnerOnQuoteChatMessage: data.notifyOwnerOnQuoteChatMessage !== false
      });
    } catch (err) {
      console.error('Error loading offer settings:', err);
      toast.error('Failed to load offer settings');
    } finally {
      setOfferSettingsLoading(false);
    }
  }, [currentBusiness?.id]);

  useEffect(() => {
    if (isOfferSettingsModalOpen) loadOfferSettings();
  }, [isOfferSettingsModalOpen, loadOfferSettings]);

  const handleSaveOfferSettings = async () => {
    if (!currentBusiness?.id || offerSettingsSaving) return;
    const num = offerSettingsDraft.defaultDepositAmount.trim() === ''
      ? 0
      : parseFloat(offerSettingsDraft.defaultDepositAmount);
    if (!Number.isFinite(num) || num < 0) {
      toast.error('Enter a valid default deposit amount');
      return;
    }
    try {
      setOfferSettingsSaving(true);
      await apiService.updateOfferSettings(currentBusiness.id, {
        requireDepositByDefault: offerSettingsDraft.requireDepositByDefault,
        defaultDepositAmount: num,
        notifyOwnerOnQuoteChatMessage: offerSettingsDraft.notifyOwnerOnQuoteChatMessage
      });
      toast.success('Offer settings saved');
      setIsOfferSettingsModalOpen(false);
    } catch (err) {
      toast.error('Failed to save offer settings');
    } finally {
      setOfferSettingsSaving(false);
    }
  };

  const loadQuoteRequests = useCallback(async () => {
    if (!currentBusiness?.id) return;
    try {
      setLoading(true);
      const resp = await apiService.getQuotes({
        businessId: currentBusiness.id,
        limit,
        offset,
        status: statusFilter || undefined,
        search: searchInput.trim() || undefined,
        sortOrder
      });
      setQuotes(resp.data ?? []);
      setPaginationTotal(resp.pagination?.total ?? 0);
    } catch (err) {
      console.error('Error loading quote requests:', err);
      setQuotes([]);
      setPaginationTotal(0);
    } finally {
      setLoading(false);
    }
  }, [currentBusiness?.id, limit, offset, statusFilter, searchInput, sortOrder]);

  useEffect(() => {
    loadQuoteRequests();
  }, [loadQuoteRequests]);

  const page = useMemo(() => Math.floor(offset / limit) + 1, [offset, limit]);
  const totalPages = useMemo(() => Math.max(1, Math.ceil((paginationTotal || 0) / limit)), [paginationTotal, limit]);

  useEffect(() => {
    setOffset(0);
  }, [searchInput, statusFilter, sortOrder]);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (isMobile) setQuoteView('grid');
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) return;
    try {
      localStorage.setItem('quoteRequestsView', quoteView);
    } catch {}
  }, [quoteView, isMobile]);

  const renderPagination = () => {
    if (loading || (quotes.length === 0 && paginationTotal === 0)) return null;
    const startItem = offset + 1;
    const endItem = offset + quotes.length;
    const hasPrevPage = offset > 0;
    const hasNextPage = offset + limit < paginationTotal;
    const showPagination = totalPages > 1 || paginationTotal > 0;
    if (!showPagination) return null;
    return (
      <div className="px-6 py-4 border-t border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="text-sm text-gray-700 text-center md:text-left">
          Showing {startItem} to {endItem} of {paginationTotal} request{paginationTotal !== 1 ? 's' : ''}
        </div>
        <div className="flex items-center justify-center gap-2">
          <button
            type="button"
            onClick={() => setOffset((o) => Math.max(0, o - limit))}
            disabled={!hasPrevPage}
            className={`px-3 py-1 text-sm rounded-lg border ${
              hasPrevPage ? 'border-gray-300 text-gray-700 hover:bg-gray-50' : 'border-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            Previous
          </button>
          <div className="flex items-center gap-1">
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (page <= 3) {
                pageNum = i + 1;
              } else if (page >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = page - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  type="button"
                  onClick={() => setOffset((pageNum - 1) * limit)}
                  className={`px-3 py-1 text-sm rounded-lg ${
                    pageNum === page ? 'bg-[#0B4778] text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => setOffset((o) => (o + limit < paginationTotal ? o + limit : o))}
            disabled={!hasNextPage}
            className={`px-3 py-1 text-sm rounded-lg border ${
              hasNextPage ? 'border-gray-300 text-gray-700 hover:bg-gray-50' : 'border-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            Next
          </button>
        </div>
      </div>
    );
  };

  const renderQuoteGridCard = (quote: QuoteRow) => {
    const quoteNames = getQuoteTypeNames(quote);
    const displayStatus = getQuoteDisplayStatus(quote);
    const quoteDetailsPath = `/dashboard/business/quote-requests/${quote.id}`;

    return (
      <div
        key={quote.id}
        className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow bg-white flex flex-col h-full relative"
      >
        <button
          type="button"
          className="md:hidden absolute inset-0 z-10 w-full h-full rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:ring-offset-1"
          onClick={() => navigate(quoteDetailsPath)}
          aria-label={`View quote request ${formatRequestNumber(quote.id)}`}
        />
        <ChevronRight className="md:hidden absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none z-20" />
        <div className="relative md:flex md:items-start md:justify-between md:gap-3">
          <div className="min-w-0 max-md:pr-28">
            <div className="font-extrabold text-gray-900 uppercase">{formatRequestNumber(quote.id)}</div>
            <div className="text-sm text-gray-600 mt-1 truncate">{getCustomerDisplayLine(quote, canViewFullPhone)}</div>
          </div>
          <span className={`inline-flex flex-shrink-0 px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap max-md:absolute max-md:right-0 max-md:top-0 ${displayStatus.badgeClass}`}>
            {displayStatus.label}
          </span>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3 items-start max-md:pr-8">
          <div>
            <div className="text-xs text-gray-500">OFFER</div>
            <div className="text-lg font-semibold text-gray-900">{formatMoney(quote.businessOfferAmount)}</div>
            {quote.requiredDepositAmount != null && Number(quote.requiredDepositAmount) > 0 && (
              <div className="text-xs text-gray-500 mt-0.5">Deposit: {formatMoney(quote.requiredDepositAmount)}</div>
            )}
          </div>
          <div>
            <div className="text-xs text-gray-500">SUBMITTED</div>
            <div className="text-sm font-semibold text-gray-800">{formatSubmittedDate(quote.createdAt)}</div>
            <div className="text-xs text-gray-500">{formatSubmittedTime(quote.createdAt)}</div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-3 text-sm max-md:pr-8">
          <div className="grid grid-cols-[4.75rem_1fr] md:block gap-x-2 items-baseline min-w-0">
            <span className="text-xs text-gray-500 shrink-0">
              <span className="md:hidden">Services:</span>
              <span className="hidden md:inline">Services</span>
            </span>
            <span className="font-semibold text-gray-800 truncate min-w-0 md:line-clamp-2 md:whitespace-normal md:block">
              {quoteNames.length > 0 ? quoteNames.join(', ') : '—'}
            </span>
          </div>
          <div className="grid grid-cols-[4.75rem_1fr] md:block gap-x-2 items-baseline min-w-0">
            <span className="text-xs text-gray-500 shrink-0">
              <span className="md:hidden">Schedule:</span>
              <span className="hidden md:inline">Preferred schedule</span>
            </span>
            <span className="font-semibold text-gray-800 truncate min-w-0 md:line-clamp-2 md:whitespace-normal md:block">
              {formatPreferredSchedule(quote.preferredSchedule)}
            </span>
            {quote.status === 'BUSINESS_OFFER_SENT' && quote.offerExpiredAt && (
              <div className="col-start-2 text-xs text-gray-500 mt-0.5 md:col-auto md:mt-0.5">
                Expires: {formatPreferredSchedule(quote.offerExpiredAt)}
              </div>
            )}
          </div>
        </div>

        <div className="hidden md:block mt-auto pt-4 w-full">
          <button
            type="button"
            onClick={() => navigate(quoteDetailsPath)}
            className="w-full flex items-center justify-center gap-1.5 px-2.5 py-2 text-sm font-medium leading-none text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#0B4778]"
            title="View details"
          >
            <Eye className="w-4 h-4 shrink-0" />
            View
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900">Quote Requests</h2>
          <p className="text-gray-600 mt-1">View and manage customer quote requests</p>
        </div>
        <div className="hidden md:flex items-center gap-2">
          <button
            type="button"
            onClick={() => setIsOfferSettingsModalOpen(true)}
            className="aacore-btn2-outline bg-gray-100 hover:bg-gray-200 flex items-center gap-2"
            title="Offer Settings"
          >
            <Settings className="w-4 h-4" />
            <span>Settings</span>
          </button>
        </div>
      </div>

      {/* Offer Settings Modal */}
      {isOfferSettingsModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setIsOfferSettingsModalOpen(false)}>
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-[#0B4778]" />
                <h3 className="text-lg font-semibold text-gray-900">Offer Settings</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsOfferSettingsModalOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="px-5 pt-3 text-sm text-gray-600">Defaults for new offers. These can be adjusted per-offer.</p>
            <div className="p-5 space-y-5">
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50/50">
                <h4 className="text-sm font-semibold text-gray-900 mb-1">Deposit default</h4>
                <p className="text-sm text-gray-600 mb-4">When you create a new offer, pre-fill deposit settings with these values.</p>
                <div className="flex items-center justify-between gap-3 mb-4">
                  <label className="text-sm font-medium text-gray-700">Require deposit by default</label>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={offerSettingsDraft.requireDepositByDefault}
                    onClick={() => setOfferSettingsDraft((prev) => ({ ...prev, requireDepositByDefault: !prev.requireDepositByDefault }))}
                    className={`relative inline-flex h-7 w-12 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:ring-offset-2 ${
                      offerSettingsDraft.requireDepositByDefault ? 'bg-[#0B4778]' : 'bg-gray-200'
                    }`}
                  >
                    <span
                      className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition ${
                        offerSettingsDraft.requireDepositByDefault ? 'translate-x-5' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Default deposit amount</label>
                  <div className="flex rounded-lg border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-[#0B4778] focus-within:border-[#0B4778]">
                    <span className="inline-flex items-center pl-3 text-gray-500">$</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={offerSettingsDraft.defaultDepositAmount}
                      onChange={(e) => setOfferSettingsDraft((prev) => ({ ...prev, defaultDepositAmount: e.target.value }))}
                      className="block w-full border-0 py-2 pr-3 text-gray-900 placeholder-gray-400 focus:ring-0 sm:text-sm rounded-r-lg"
                      placeholder="0"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">Used as the starting point for the offer editor.</p>
                </div>
                <div className="pt-4 mt-4 border-t border-gray-200">
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <label className="text-sm font-medium text-gray-700">Email on new quote chat messages</label>
                      <p className="text-xs text-gray-500 mt-0.5 max-w-[240px] sm:max-w-none">
                        Send an email to your business when a customer sends a message on a quote request.
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={offerSettingsDraft.notifyOwnerOnQuoteChatMessage}
                      onClick={() =>
                        setOfferSettingsDraft((prev) => ({
                          ...prev,
                          notifyOwnerOnQuoteChatMessage: !prev.notifyOwnerOnQuoteChatMessage
                        }))
                      }
                      className={`relative inline-flex h-7 w-12 flex-shrink-0 rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:ring-offset-2 ${
                        offerSettingsDraft.notifyOwnerOnQuoteChatMessage ? 'bg-[#0B4778]' : 'bg-gray-200'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition ${
                          offerSettingsDraft.notifyOwnerOnQuoteChatMessage ? 'translate-x-5' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-gray-200">
              <button type="button" onClick={() => setIsOfferSettingsModalOpen(false)} className="aacore-btn2-secondary inline-flex items-center gap-2">
                <X className="w-4 h-4" />
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveOfferSettings}
                disabled={offerSettingsSaving || offerSettingsLoading}
                className="aacore-btn2-primary flex items-center gap-2"
              >
                {offerSettingsSaving ? (
                  'Saving...'
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mobile: collapsible filters */}
      <div className="md:hidden">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <button
            type="button"
            onClick={() => setIsFilterSectionOpen(!isFilterSectionOpen)}
            className="w-full flex items-center justify-between px-4 py-3 bg-[#0B4778] text-white hover:bg-[#093a63] transition-colors"
            aria-expanded={isFilterSectionOpen}
          >
            <span className="flex items-center gap-2 text-sm font-semibold">
              <SlidersHorizontal className="w-4 h-4" />
              Filters
            </span>
            <span className="flex items-center gap-2 text-sm font-medium">
              {paginationTotal} request{paginationTotal !== 1 ? 's' : ''}
              <ChevronDown className={`w-5 h-5 text-white/90 transition-transform ${isFilterSectionOpen ? 'rotate-180' : ''}`} />
            </span>
          </button>

          {isFilterSectionOpen && (
            <div className="p-4 space-y-3 border-t border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search customer, request #, address, or details..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className={`w-full pl-10 ${searchInput ? 'pr-10' : 'pr-3'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]`}
                />
                {searchInput && (
                  <button
                    type="button"
                    onClick={() => setSearchInput('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    aria-label="Clear search"
                  >
                    <span className="text-lg leading-none">&times;</span>
                  </button>
                )}
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white text-gray-900"
                aria-label="Filter by status"
              >
                <option value="">All statuses</option>
                <option value="NEW">{formatStatusLabel('NEW')}</option>
                <option value="BUSINESS_OFFER_SENT">{formatStatusLabel('BUSINESS_OFFER_SENT')}</option>
                <option value={QUOTE_OFFER_EXPIRED_FILTER}>Offer Expired</option>
                <option value="BUSINESS_OFFER_ACCEPTED">{formatStatusLabel('BUSINESS_OFFER_ACCEPTED')}</option>
                <option value={QUOTE_APPOINTMENT_CREATED_FILTER}>Appointment Created</option>
                <option value={QUOTE_PENDING_DEPOSIT_FILTER}>Pending Deposit</option>
                <option value="DEPOSIT_RECEIVED">{formatStatusLabel('DEPOSIT_RECEIVED')}</option>
                <option value={QUOTE_APPOINTMENT_CANCELED_FILTER}>Appointment Canceled</option>
              </select>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value as SortOption)}
                className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white text-gray-900"
                aria-label="Sort order"
              >
                <option value="newest">Newest first</option>
                <option value="oldest">Oldest first</option>
              </select>
            </div>
          )}
        </div>
      </div>

      {/* Desktop: always-visible filters */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[12rem] flex-1 basis-[16rem] max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search customer, request #, address, or details..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className={`w-full pl-10 ${searchInput ? 'pr-10' : 'pr-3'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]`}
            />
            {searchInput && (
              <button
                type="button"
                onClick={() => setSearchInput('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                aria-label="Clear search"
              >
                <span className="text-lg leading-none">&times;</span>
              </button>
            )}
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="min-w-[9.5rem] flex-none pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white text-gray-900"
            aria-label="Filter by status"
          >
            <option value="">All statuses</option>
            <option value="NEW">{formatStatusLabel('NEW')}</option>
            <option value="BUSINESS_OFFER_SENT">{formatStatusLabel('BUSINESS_OFFER_SENT')}</option>
            <option value={QUOTE_OFFER_EXPIRED_FILTER}>Offer Expired</option>
            <option value="BUSINESS_OFFER_ACCEPTED">{formatStatusLabel('BUSINESS_OFFER_ACCEPTED')}</option>
            <option value={QUOTE_APPOINTMENT_CREATED_FILTER}>Appointment Created</option>
            <option value={QUOTE_PENDING_DEPOSIT_FILTER}>Pending Deposit</option>
            <option value="DEPOSIT_RECEIVED">{formatStatusLabel('DEPOSIT_RECEIVED')}</option>
            <option value={QUOTE_APPOINTMENT_CANCELED_FILTER}>Appointment Canceled</option>
          </select>

          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as SortOption)}
            className="min-w-[9.5rem] flex-none pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white text-gray-900"
            aria-label="Sort order"
          >
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>

          <div className="flex flex-wrap items-center gap-2 flex-none ml-auto">
            <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-700 text-sm font-medium">
              <Building2 className="w-4 h-4 text-gray-500" />
              {paginationTotal} request{paginationTotal !== 1 ? 's' : ''}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setQuoteView('grid')}
                title="Grid View"
                className={`px-3 py-2 rounded-lg text-sm flex items-center justify-center ${
                  quoteView === 'grid' ? 'bg-[#0B4778] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setQuoteView('table')}
                title="List View"
                className={`px-3 py-2 rounded-lg text-sm flex items-center justify-center ${
                  quoteView === 'table' ? 'bg-[#0B4778] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {quoteView === 'grid' ? (
        <div className="max-md:bg-transparent max-md:border-0 max-md:shadow-none max-md:p-0 md:bg-white md:rounded-lg md:shadow-sm md:border md:border-gray-200 md:p-4">
          {loading ? (
            <div className="flex justify-center items-center py-16">
              <LoadingSpinner />
            </div>
          ) : quotes.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <div className="text-gray-500">
                <FileText className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                <div className="font-semibold text-gray-700">No quote requests</div>
                <div className="text-sm text-gray-500 mt-1">
                  {searchInput.trim() || statusFilter
                    ? 'No requests match your search or filter.'
                    : 'Customer quote requests will appear here.'}
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {quotes.map((quote) => renderQuoteGridCard(quote))}
            </div>
          )}
          {renderPagination()}
        </div>
      ) : (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center py-16">
            <LoadingSpinner />
          </div>
        ) : quotes.length === 0 && !loading ? (
          <div className="px-6 py-12 text-center text-gray-500">
            <p className="font-medium text-gray-700">No quote requests</p>
            <p className="text-sm mt-1">
              {searchInput.trim() || statusFilter
                ? 'No requests match your search or filter.'
                : 'Customer quote requests will appear here.'}
            </p>
          </div>
        ) : (
          <>
          <div className="overflow-x-auto w-full">
            <table className="w-full divide-y divide-gray-200 border border-gray-200 border-collapse">
              <thead className="bg-gray-50">
                <tr className="divide-x divide-gray-200">
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Request #
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Services
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Preferred schedule
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Submitted
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    Status
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Offer
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {quotes.map((quote) => {
                  const quoteNames = getQuoteTypeNames(quote);
                  const displayStatus = getQuoteDisplayStatus(quote);
                  return (
                    <tr key={quote.id} className="hover:bg-gray-50 divide-x divide-gray-200">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 uppercase">
                        {formatRequestNumber(quote.id)}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <span className="font-medium text-gray-900">{getCustomerDisplayLine(quote, canViewFullPhone)}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        {quoteNames.length > 0 ? (
                          <div className="flex flex-col">
                            <span className="line-clamp-4 block">{quoteNames.join(', ')}</span>
                            {quoteNames.length > 1 && (
                              <span className="text-xs text-gray-500">Multiple requested</span>
                            )}
                          </div>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <span className="line-clamp-4 block">{formatPreferredSchedule(quote.preferredSchedule)}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <div className="flex flex-col">
                          <span>{formatSubmittedDate(quote.createdAt)}</span>
                          <span className="text-gray-500">{formatSubmittedTime(quote.createdAt)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full whitespace-nowrap ${displayStatus.badgeClass}`}>
                          {displayStatus.label}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-700">
                        <div className="flex flex-col gap-0.5">
                          <span>{formatMoney(quote.businessOfferAmount)}</span>
                          {quote.status === 'BUSINESS_OFFER_SENT' && quote.offerExpiredAt && (
                            <span className="text-xs text-gray-500">
                              Expires: {formatPreferredSchedule(quote.offerExpiredAt)}
                            </span>
                          )}
                          {quote.requiredDepositAmount != null && Number(quote.requiredDepositAmount) > 0 && (
                            <span className="text-xs text-gray-500">Deposit Required: {formatMoney(quote.requiredDepositAmount)}</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          type="button"
                          onClick={() => navigate(`/dashboard/business/quote-requests/${quote.id}`)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#0B4778]"
                          title="View details"
                        >
                          <Eye className="w-4 h-4" />
                          View
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {renderPagination()}
          </>
        )}
      </div>
      )}
    </div>
  );
};

export default QuoteRequestsSection;
